import React, { useState, useEffect, useCallback } from 'react';
import { Users, Shield, Key, Trash2, Edit3, AlertCircle, CheckCircle2, Package, Sparkles, Check, Cpu, HardDrive, Layers, Server, UserPlus, X } from 'lucide-react';
import api from '../services/api';
import '../styles/admin.css';
import '../styles/super-admin.css';

function Admin({ onStatusMessage, user }) {
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingUsername, setDeletingUsername] = useState(null);

  // System Users List, Quota & Form State
  const [users, setUsers] = useState([]);
  const [quota, setQuota] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form Fields
  const [userFullName, setUserFullName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userRole, setUserRole] = useState('developer');
  const [quotaDeployments, setQuotaDeployments] = useState('');
  const [quotaMemoryMB, setQuotaMemoryMB] = useState('');
  const [quotaCpu, setQuotaCpu] = useState('');
  const [quotaStorageGB, setQuotaStorageGB] = useState('');

  const [editingUser, setEditingUser] = useState(null);
  const [userFilterText, setUserFilterText] = useState('');
  const [userSortBy, setUserSortBy] = useState('username');
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Load system users and package quota
  const loadData = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const effectiveRole = user?.role || api.user?.role;
      const canUseAdminApis = effectiveRole === 'admin' || effectiveRole === 'superadmin';
      if (!canUseAdminApis) {
        setUsers([]);
        setQuota(null);
        return;
      }

      const userData = await api.getAdminUsers().catch(err => {
        if (err.status === 403 || err.message?.includes('feature requires')) return { users: [], quota: null };
        throw err;
      });

      setUsers(userData.users || []);
      if (userData.quota) {
        setQuota(userData.quota);
      }
    } catch (err) {
      onStatusMessage('Failed to load system users: ' + (err.message || 'Server error'), 'error');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [onStatusMessage, user?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Password validation rules
  const pwdRules = {
    length: userPassword.length >= 8,
    upper: /[A-Z]/.test(userPassword),
    lower: /[a-z]/.test(userPassword),
    number: /[0-9]/.test(userPassword),
    special: /[^A-Za-z0-9]/.test(userPassword)
  };
  const isPasswordValid = Object.values(pwdRules).every(Boolean);

  // Helper: Generate Random Strong Password
  const handleGeneratePassword = () => {
    const charsUpper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const charsLower = 'abcdefghjkmnpqrstuvwxyz';
    const charsNum = '23456789';
    const charsSpecial = '!@#$%^&*';

    let pwd = '';
    pwd += charsUpper.charAt(Math.floor(Math.random() * charsUpper.length));
    pwd += charsLower.charAt(Math.floor(Math.random() * charsLower.length));
    pwd += charsNum.charAt(Math.floor(Math.random() * charsNum.length));
    pwd += charsSpecial.charAt(Math.floor(Math.random() * charsSpecial.length));

    const all = charsUpper + charsLower + charsNum + charsSpecial;
    for (let i = 0; i < 8; i++) {
      pwd += all.charAt(Math.floor(Math.random() * all.length));
    }
    // Shuffle password
    pwd = pwd.split('').sort(() => 0.5 - Math.random()).join('');

    setUserPassword(pwd);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(pwd).catch(() => {});
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 3000);
    }
  };

  // User Form Actions
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const trimmedFullName = userFullName.trim();
    const trimmedEmail = userEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      onStatusMessage('Valid email address is required', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      onStatusMessage('Please enter a valid email address (e.g. user@example.com)', 'error');
      return;
    }

    if (!editingUser) {
      if (!userPassword) {
        onStatusMessage('Temporary password is required for new team members', 'error');
        return;
      }
      if (!isPasswordValid) {
        onStatusMessage('Password must meet all security requirements (min 8 chars, uppercase, lowercase, number, symbol)', 'error');
        return;
      }
      if (quota && !quota.allowed) {
        onStatusMessage(`Team member limit reached for your plan (${quota.currentCount}/${quota.maxTeamMembers}). Upgrade your package or remove existing members.`, 'error');
        return;
      }
    } else {
      if (userPassword && !isPasswordValid) {
        onStatusMessage('New password must meet all security requirements', 'error');
        return;
      }
    }

    const customLimits = {
      fullName: trimmedFullName || undefined,
      maxDeployments: quotaDeployments !== '' ? Number(quotaDeployments) : null,
      memoryLimitMB: quotaMemoryMB !== '' ? Number(quotaMemoryMB) : null,
      cpuLimit: quotaCpu !== '' ? Number(quotaCpu) : null,
      storageGB: quotaStorageGB !== '' ? Number(quotaStorageGB) : null
    };

    setIsSubmitting(true);
    try {
      if (editingUser) {
        await api.updateAdminUser(editingUser.username, {
          email: trimmedEmail,
          fullName: trimmedFullName,
          password: userPassword || undefined,
          role: userRole,
          customLimits
        });
        onStatusMessage('Team member updated successfully', 'success');
      } else {
        await api.createAdminUser({
          fullName: trimmedFullName,
          email: trimmedEmail,
          password: userPassword,
          role: userRole,
          customLimits
        });
        onStatusMessage('Team member created successfully! Credentials saved.', 'success');
      }
      handleUserClear();
      setIsModalOpen(false);
      await loadData(true);
    } catch (err) {
      onStatusMessage('Error: ' + err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUserDelete = async (u) => {
    const activeUsername = user?.username || api.user?.username;
    if (u.username === activeUsername) {
      onStatusMessage('You cannot delete your own account', 'error');
      return;
    }
    if (deletingUsername) return;
    if (!window.confirm(`Delete team member "${u.customLimits?.fullName || u.username}"?`)) return;

    setDeletingUsername(u.username);
    try {
      await api.deleteAdminUser(u.username);
      onStatusMessage('Team member deleted successfully', 'success');
      await loadData(true);
    } catch (err) {
      onStatusMessage('Error: ' + err.message, 'error');
    } finally {
      setDeletingUsername(null);
    }
  };

  const handleUserEdit = (u) => {
    setEditingUser(u);
    setUserFullName(u.customLimits?.fullName || '');
    setUserEmail(u.email || (u.username?.includes('@') ? u.username : ''));
    setUserRole(u.role || 'developer');
    setUserPassword('');

    const cl = u.customLimits || {};
    setQuotaDeployments(cl.maxDeployments !== null && cl.maxDeployments !== undefined ? String(cl.maxDeployments) : '');
    setQuotaMemoryMB(cl.memoryLimitMB !== null && cl.memoryLimitMB !== undefined ? String(cl.memoryLimitMB) : '');
    setQuotaCpu(cl.cpuLimit !== null && cl.cpuLimit !== undefined ? String(cl.cpuLimit) : '');
    setQuotaStorageGB(cl.storageGB !== null && cl.storageGB !== undefined ? String(cl.storageGB) : '');
    setIsModalOpen(true);
  };

  const handleUserClear = () => {
    setEditingUser(null);
    setUserFullName('');
    setUserEmail('');
    setUserPassword('');
    setUserRole('developer');
    setQuotaDeployments('');
    setQuotaMemoryMB('');
    setQuotaCpu('');
    setQuotaStorageGB('');
  };

  const activeUsername = user?.username || api.user?.username;
  const effectiveMaxMembers = quota?.maxTeamMembers ?? 5;
  const createdMembersCount = quota?.currentCount ?? users.filter(u => u.username !== activeUsername).length;
  const remainingMembersCount = quota?.remaining ?? Math.max(0, effectiveMaxMembers - createdMembersCount);
  const isQuotaExceeded = !editingUser && (quota ? !quota.allowed : createdMembersCount >= effectiveMaxMembers);
  const usedPercentage = Math.min(100, Math.round((createdMembersCount / effectiveMaxMembers) * 100));

  const filteredUsers = [...users]
    .filter((u) => {
      const q = (userFilterText || '').trim().toLowerCase();
      if (!q) return true;
      const fn = (u.customLimits?.fullName || '').toLowerCase();
      const em = (u.email || '').toLowerCase();
      const un = (u.username || '').toLowerCase();
      const ro = (u.role || '').toLowerCase();
      return fn.includes(q) || em.includes(q) || un.includes(q) || ro.includes(q);
    })
    .sort((a, b) => {
      if (userSortBy === 'username') {
        const nameA = a.customLimits?.fullName || a.username || '';
        const nameB = b.customLimits?.fullName || b.username || '';
        return nameA.localeCompare(nameB);
      }
      return (a.role || '').localeCompare(b.role || '');
    });

  if (loading) {
    return (
      <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading administration dashboard...
      </div>
    );
  }

  return (
    <div className="dashboard-layout-wrapper" style={{ maxWidth: '1200px', margin: '0 auto', padding: '1.5rem 2rem' }}>
      {/* Header Section */}
      <div className="dashboard-header" style={{ marginBottom: '1.5rem' }}>
        <div className="dashboard-header-title-area" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', width: '100%' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>Team Management</h2>
              <p style={{ color: 'var(--text-muted)', margin: '0.35rem 0 0 0', fontSize: '0.9rem' }}>
                Manage organization team members, configure per-user resource quotas, and assign access.
              </p>
            </div>

            {/* Header Member Limit Summary Badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--surface)', padding: '0.65rem 1.25rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <Package size={20} color="var(--primary)" />
              <div style={{ fontSize: '0.88rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Seats: </span>
                <strong style={{ color: 'var(--text-main)' }}>{createdMembersCount} of {effectiveMaxMembers} Used</strong>
                <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', color: remainingMembersCount > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                  ({remainingMembersCount} Available)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Package Quota & Capacity Banner Card */}
      <div className="card" style={{ padding: '1.5rem 2rem', marginBottom: '1.75rem', borderRadius: 'var(--radius-lg)', background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: 700 }}>{quota?.tierName || 'Subscription'} Quota</h3>
                <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: '12px', background: 'rgba(var(--primary-rgb), 0.12)', color: 'var(--primary)', fontWeight: 600, textTransform: 'uppercase' }}>
                  {quota?.tierName || 'Active'}
                </span>
              </div>
              <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Your subscription package permits up to <strong>{effectiveMaxMembers} team members</strong> in your organization.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {remainingMembersCount > 0 ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--success)', background: 'rgba(var(--success-rgb), 0.1)', padding: '0.4rem 0.9rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>
                <CheckCircle2 size={16} />
                <span>{remainingMembersCount} Member Seat{remainingMembersCount > 1 ? 's' : ''} Remaining</span>
              </div>
            ) : (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--danger)', background: 'rgba(var(--danger-rgb), 0.1)', padding: '0.4rem 0.9rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>
                <AlertCircle size={16} />
                <span>All Seats Occupied ({createdMembersCount}/{effectiveMaxMembers})</span>
              </div>
            )}
          </div>
        </div>

        {/* Quota Progress Bar */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: '0.4rem', color: 'var(--text-muted)' }}>
            <span>Capacity Allocation</span>
            <span><strong>{createdMembersCount}</strong> of <strong>{effectiveMaxMembers}</strong> seats used ({usedPercentage}%)</span>
          </div>
          <div style={{ height: '8px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${usedPercentage}%`,
                background: remainingMembersCount === 0 ? 'var(--danger)' : (usedPercentage > 80 ? '#f59e0b' : 'var(--primary)'),
                borderRadius: '4px',
                transition: 'width 0.4s ease'
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Full-Width Team Members Section */}
      <section className="dashboard-stats-wrapper" style={{ padding: '0', border: 'none', background: 'transparent' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              Members
              <span style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.2rem 0.65rem', borderRadius: '12px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' }}>
                {createdMembersCount} / {effectiveMaxMembers}
              </span>
            </h2>

            {/* Search */}
            <div className="dashboard-search-wrapper" style={{ margin: 0 }}>
              <span className="dashboard-search-icon-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              </span>
              <input
                type="text"
                className="dashboard-search-input"
                placeholder="Search members..."
                value={userFilterText}
                onChange={(e) => setUserFilterText(e.target.value)}
                style={{ height: '40px', fontSize: '0.88rem', width: '220px' }}
              />
            </div>

            {/* Sort */}
            <select
              value={userSortBy}
              onChange={(e) => setUserSortBy(e.target.value)}
              style={{ height: '40px', padding: '0 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)', cursor: 'pointer', outline: 'none', fontSize: '0.88rem' }}
            >
              <option value="username">Sort: Name</option>
              <option value="role">Sort: Role</option>
            </select>
          </div>

          {/* Add Team Member Primary Button - Pinned to the Right */}
          <button
            onClick={() => { handleUserClear(); setIsModalOpen(true); }}
            disabled={isQuotaExceeded}
            style={{
              height: '40px',
              padding: '0 1.25rem',
              borderRadius: 'var(--radius-md)',
              background: isQuotaExceeded ? 'var(--border)' : 'var(--primary)',
              color: isQuotaExceeded ? 'var(--text-muted)' : '#fff',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              cursor: isQuotaExceeded ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.45rem',
              boxShadow: isQuotaExceeded ? 'none' : '0 4px 12px rgba(var(--primary-rgb), 0.25)',
              transition: 'all 0.2s',
              marginLeft: 'auto'
            }}
            onMouseOver={(e) => { if (!isQuotaExceeded) e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={(e) => { if (!isQuotaExceeded) e.currentTarget.style.transform = 'none'; }}
          >
            <UserPlus size={16} />
            <span>{isQuotaExceeded ? 'Seat Limit Reached' : 'Add Team Member'}</span>
          </button>
        </div>

        {/* Member Cards List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {users.length === 0 ? (
            <div className="card" style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--text-muted)', borderRadius: 'var(--radius-lg)' }}>
              <Users size={40} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
              <div style={{ fontWeight: 600, fontSize: '1.05rem', color: 'var(--text-main)' }}>No team members yet</div>
              <div style={{ fontSize: '0.88rem', marginTop: '0.25rem' }}>Click "Add Team Member" above to invite developers and assign resource quotas.</div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', borderRadius: 'var(--radius-lg)' }}>
              No members match your search criteria.
            </div>
          ) : (
            filteredUsers.map((u) => {
              const isActiveUser = u.username === activeUsername;
              const cl = u.customLimits || {};
              const hasCustomLimits = cl.maxDeployments || cl.memoryLimitMB || cl.cpuLimit || cl.storageGB;
              const displayName = cl.fullName || u.username;

              return (
                <div key={u.username} className="card" style={{ padding: '1.25rem 1.5rem', borderRadius: 'var(--radius-lg)', background: 'var(--surface)', border: `1px solid ${isActiveUser ? 'var(--primary)' : 'var(--border)'}`, transition: 'all 0.2s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                      <div style={{
                        width: '46px',
                        height: '46px',
                        borderRadius: '12px',
                        background: u.role === 'admin' ? 'rgba(var(--danger-rgb), 0.1)' : 'rgba(var(--success-rgb), 0.1)',
                        color: u.role === 'admin' ? 'var(--danger)' : 'var(--success)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {u.role === 'admin' ? <Shield size={22} /> : <Users size={22} />}
                      </div>

                      <div>
                        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span>{displayName}</span>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                            (@{u.username})
                          </span>
                          {isActiveUser && (
                            <span style={{ fontSize: '0.65rem', padding: '0.15rem 0.5rem', borderRadius: '4px', background: 'rgba(var(--primary-rgb), 0.15)', color: 'var(--primary)', textTransform: 'uppercase', fontWeight: 700 }}>
                              You (Owner)
                            </span>
                          )}
                        </div>

                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{u.email || 'No email specified'}</span>
                          <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>• {u.role}</span>
                          {u.apiKey && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--accent)', fontSize: '0.78rem' }}>
                              <Key size={12} /> API Access
                            </span>
                          )}
                        </div>

                        {/* Resource Quota Badges */}
                        <div style={{ marginTop: '0.65rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, marginRight: '0.2rem' }}>
                            Quotas:
                          </span>
                          {cl.maxDeployments ? (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px', background: 'rgba(var(--primary-rgb), 0.08)', color: 'var(--primary)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                              <Layers size={12} /> Max {cl.maxDeployments} Apps
                            </span>
                          ) : null}
                          {cl.memoryLimitMB ? (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                              <Server size={12} /> {cl.memoryLimitMB} MB RAM
                            </span>
                          ) : null}
                          {cl.cpuLimit ? (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px', background: 'rgba(234, 88, 12, 0.1)', color: '#ea580c', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                              <Cpu size={12} /> {cl.cpuLimit} CPU Cores
                            </span>
                          ) : null}
                          {cl.storageGB ? (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                              <HardDrive size={12} /> {cl.storageGB} GB Disk
                            </span>
                          ) : null}
                          {!hasCustomLimits && (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px', background: 'var(--surface-hover)', color: 'var(--text-muted)', fontWeight: 500 }}>
                              Shared Organization Pool
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {!isActiveUser ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <button
                          onClick={() => handleUserEdit(u)}
                          title="Edit member limits & details"
                          style={{
                            height: '38px',
                            padding: '0 1rem',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--surface-hover)',
                            color: 'var(--text-main)',
                            border: '1px solid var(--border)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            transition: 'all 0.2s'
                          }}
                        >
                          <Edit3 size={15} />
                          <span>Edit</span>
                        </button>

                        <button
                          onClick={() => handleUserDelete(u)}
                          disabled={deletingUsername === u.username}
                          title={deletingUsername === u.username ? "Deleting..." : 'Delete user'}
                          style={{
                            height: '38px',
                            width: '38px',
                            borderRadius: 'var(--radius-md)',
                            background: 'rgba(239, 68, 68, 0.08)',
                            color: 'var(--danger)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            cursor: deletingUsername === u.username ? 'not-allowed' : 'pointer',
                            opacity: deletingUsername === u.username ? 0.4 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic', padding: '0.4rem 0.75rem', borderRadius: '6px', background: 'var(--surface-2)' }}>
                        Primary Administrator
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* POPUP MODAL: Add / Edit Team Member */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSubmitting) {
              setIsModalOpen(false);
            }
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '18px',
              width: '100%',
              maxWidth: '580px',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh'
            }}
          >
            {/* Modal Header */}
            <div style={{ padding: '1.25rem 1.75rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface-2)' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)' }}>
                  {editingUser ? `Edit Member: ${editingUser.customLimits?.fullName || editingUser.username}` : 'Add New Team Member'}
                </h3>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {editingUser ? 'Update role or allocated resource limits.' : `Seat Capacity: ${createdMembersCount} of ${effectiveMaxMembers} seats used (${remainingMembersCount} remaining)`}
                </p>
              </div>

              <button
                type="button"
                onClick={() => { if (!isSubmitting) setIsModalOpen(false); }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <form onSubmit={handleUserSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>
                {/* Full Name & Email in 2 columns */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label htmlFor="modalUserFullName" style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="modalUserFullName"
                      placeholder="e.g. Ali Khan"
                      required
                      disabled={isSubmitting}
                      value={userFullName}
                      onChange={(e) => setUserFullName(e.target.value)}
                      style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)', outline: 'none', fontSize: '0.9rem' }}
                    />
                  </div>

                  <div>
                    <label htmlFor="modalUserEmail" style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="modalUserEmail"
                      placeholder="e.g. ali@company.com"
                      required
                      disabled={isSubmitting}
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)', outline: 'none', fontSize: '0.9rem' }}
                    />
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label htmlFor="modalUserRole" style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>Role</label>
                  {(user?.role === 'superadmin' || api.user?.role === 'superadmin') ? (
                    <select
                      id="modalUserRole"
                      value={userRole}
                      disabled={isSubmitting}
                      onChange={(e) => setUserRole(e.target.value)}
                      style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)', outline: 'none', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                      <option value="developer">Developer</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    <div style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface-hover)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', cursor: 'not-allowed', fontSize: '0.9rem' }}>
                      Developer
                    </div>
                  )}
                </div>

                {/* Password Field with Generator Button */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                      {editingUser ? 'New Password (Optional)' : 'Temporary Password'}
                    </label>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      disabled={isSubmitting}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--primary)',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        padding: '0.15rem 0.35rem'
                      }}
                      title="Generate a secure strong password"
                    >
                      {passwordCopied ? <Check size={14} color="var(--success)" /> : <Sparkles size={14} />}
                      <span>{passwordCopied ? 'Copied to Clipboard!' : 'Generate Strong Password'}</span>
                    </button>
                  </div>

                  <input
                    type="text"
                    id="modalUserPassword"
                    placeholder={editingUser ? '•••••••• (leave blank to keep current)' : 'Enter or generate strong password'}
                    required={!editingUser}
                    disabled={isSubmitting}
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                    style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)', outline: 'none', fontFamily: userPassword ? 'monospace' : 'inherit', fontSize: '0.9rem' }}
                  />

                  {/* Password Requirement Checklist */}
                  {(userPassword || !editingUser) && (
                    <div style={{ marginTop: '0.5rem', padding: '0.65rem 0.85rem', background: 'var(--surface-hover)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ fontWeight: 600, marginBottom: '0.2rem', color: 'var(--text-muted)' }}>Security Requirements:</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem' }}>
                        <span style={{ color: pwdRules.length ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          {pwdRules.length ? '✓' : '○'} 8+ characters
                        </span>
                        <span style={{ color: pwdRules.upper ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          {pwdRules.upper ? '✓' : '○'} Uppercase (A-Z)
                        </span>
                        <span style={{ color: pwdRules.lower ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          {pwdRules.lower ? '✓' : '○'} Lowercase (a-z)
                        </span>
                        <span style={{ color: pwdRules.number && pwdRules.special ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          {pwdRules.number && pwdRules.special ? '✓' : '○'} Number & Symbol
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Resource Allocation Box */}
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '1rem', background: 'var(--surface)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)', marginBottom: '0.2rem' }}>
                    <Layers size={16} color="var(--primary)" />
                    <span>Resource Allocation (Per Member)</span>
                  </div>
                  <p style={{ margin: '0 0 0.85rem 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Set limits for this user. Leave empty to allow sharing your plan pool.
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        Max Deployments
                      </label>
                      <input
                        type="number"
                        min="1"
                        placeholder="e.g. 2"
                        value={quotaDeployments}
                        disabled={isSubmitting}
                        onChange={(e) => setQuotaDeployments(e.target.value)}
                        style={{ width: '100%', height: '38px', padding: '0 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface-hover)', color: 'var(--text-main)', outline: 'none', fontSize: '0.85rem' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        RAM (MB)
                      </label>
                      <input
                        type="number"
                        min="128"
                        step="128"
                        placeholder="e.g. 1024"
                        value={quotaMemoryMB}
                        disabled={isSubmitting}
                        onChange={(e) => setQuotaMemoryMB(e.target.value)}
                        style={{ width: '100%', height: '38px', padding: '0 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface-hover)', color: 'var(--text-main)', outline: 'none', fontSize: '0.85rem' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        CPU Cores
                      </label>
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        placeholder="e.g. 1.0"
                        value={quotaCpu}
                        disabled={isSubmitting}
                        onChange={(e) => setQuotaCpu(e.target.value)}
                        style={{ width: '100%', height: '38px', padding: '0 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface-hover)', color: 'var(--text-main)', outline: 'none', fontSize: '0.85rem' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        Storage (GB)
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="e.g. 5"
                        value={quotaStorageGB}
                        disabled={isSubmitting}
                        onChange={(e) => setQuotaStorageGB(e.target.value)}
                        style={{ width: '100%', height: '38px', padding: '0 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface-hover)', color: 'var(--text-main)', outline: 'none', fontSize: '0.85rem' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '1.25rem 1.75rem', background: 'var(--surface-2)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                  style={{
                    height: '42px',
                    padding: '0 1.25rem',
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    height: '42px',
                    padding: '0 1.5rem',
                    background: isSubmitting ? 'var(--border)' : 'var(--primary)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(var(--primary-rgb), 0.25)',
                    transition: 'all 0.2s'
                  }}
                >
                  {isSubmitting
                    ? (editingUser ? 'Saving...' : 'Creating...')
                    : (editingUser ? 'Save Changes' : 'Create Team Member')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
